import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner } from '@klaro7/ui';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { usePermissions } from '@/features/rbac/usePermissions';
import { RoleGuard } from '@/features/rbac/RoleGuard';
import { Permission, UserRole } from '@klaro7/shared-types';
import { useUserStats } from '@/features/users/hooks/useAdminUsers';
import { useWallet } from '@/features/wallet/hooks/useWallet';
import { hasCompletedFirstRecharge } from '@/features/wallet/lib/firstRechargePromo';
import { useCallHistory } from '@/features/calls/hooks/useCallHistory';
import { callStatusInfo } from '@/features/calls/lib/callStatusInfo';
import { ConsultantDashboardHome } from '@/features/professional-profile/components/ConsultantDashboardHome';

function formatActivityDate(value: string | Date): string {
  return new Date(value).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DashboardHomePage() {
  const { user, hasRole } = useAuth();
  const { isAdmin } = usePermissions();

  const firstName = user?.profile?.firstName || 'Utente';

  const todayFormatted = new Date().toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  if (isAdmin) {
    return <AdminDashboard firstName={firstName} todayFormatted={todayFormatted} />;
  }

  if (hasRole(UserRole.PROFESSIONAL)) {
    return <ConsultantDashboardHome />;
  }

  return <UserDashboard firstName={firstName} todayFormatted={todayFormatted} user={user} />;
}

const activityTypeLabels: Record<string, string> = {
  audio: 'Chiamata',
  video: 'Videochiamata',
  chat: 'Chat',
};

function AdminDashboard({
  firstName,
  todayFormatted,
}: {
  firstName: string;
  todayFormatted: string;
}) {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canOpenDetail = hasRole(UserRole.SUPER_ADMIN);
  const { data: stats, isLoading: statsLoading } = useUserStats();
  const { data: recent, isLoading: recentLoading } = useCallHistory({ limit: 5 });
  const recentSessions = recent?.data ?? [];

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 p-8">
        <div className="relative z-10">
          <h1 className="text-2xl font-bold text-white">Benvenuto, {firstName}</h1>
          <p className="mt-1 text-brand-cyan">{todayFormatted}</p>
        </div>
        <div className="absolute right-0 top-0 h-full w-1/3 opacity-10">
          <svg viewBox="0 0 200 200" className="h-full w-full" fill="none">
            <circle cx="160" cy="40" r="60" fill="white" />
            <circle cx="100" cy="140" r="80" fill="white" />
            <circle cx="180" cy="160" r="40" fill="white" />
          </svg>
        </div>
      </div>

      {/* Stats Row — fed by GET /users/stats (utenti reali della collezione User) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatsCard
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          }
          label="Utenti"
          value={statsLoading ? undefined : stats?.users}
          color="primary"
        />
        <StatsCard
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          }
          label="Utenti attivi"
          value={statsLoading ? undefined : stats?.activeUsers}
          color="info"
        />
        <StatsCard
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          }
          label="Professionisti"
          value={statsLoading ? undefined : stats?.professionals}
          color="secondary"
        />
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-neutral-900">Azioni Rapide</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <QuickActionCard
            to="/dashboard/admin/users"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
            title="Gestione Utenti"
            description="Visualizza e gestisci gli utenti"
            color="primary"
          />
          <QuickActionCard
            to="/dashboard/admin/professionals"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
            title="Gestione Professionisti"
            description="Visualizza e gestisci i professionisti"
            color="secondary"
          />
          <QuickActionCard
            to="/dashboard/admin/waitlist"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            }
            title="Lista d'Attesa"
            description="Visualizza gli iscritti dalla landing"
            color="warning"
          />
          <QuickActionCard
            to="/dashboard/admin/questionnaires"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            }
            title="Questionari"
            description="Revisiona i questionari professionisti"
            color="info"
          />
          <RoleGuard requiredPermission={Permission.SETTINGS_MANAGE}>
            <QuickActionCard
              to="/dashboard/admin/services"
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              }
              title="Servizi Esterni"
              description="Configura integrazioni e API"
              color="violet"
            />
          </RoleGuard>
        </div>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Attività Recenti</CardTitle>
              <CardDescription>Le ultime consulenze sulla piattaforma</CardDescription>
            </div>
            <Link
              to="/dashboard/admin/activities"
              className="shrink-0 text-sm font-medium text-brand-teal hover:underline"
            >
              Vedi tutte →
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {recentLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : recentSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mb-4 rounded-full bg-neutral-100 p-4">
                <svg className="h-8 w-8 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="font-medium text-neutral-600">Nessuna attività recente</p>
              <p className="mt-1 text-sm text-neutral-600">Le attività recenti appariranno qui</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentSessions.map((session) => {
                const statusInfo = callStatusInfo(session);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const caller = session.callerId as any;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const pro = session.professionalId as any;
                const callerName = caller?.profile
                  ? `${caller.profile.firstName ?? ''} ${caller.profile.lastName ?? ''}`.trim()
                  : caller?.email ?? 'Utente';
                const proName = pro?.userId?.profile
                  ? `${pro.userId.profile.firstName ?? ''} ${pro.userId.profile.lastName ?? ''}`.trim()
                  : 'Professionista';
                return (
                  <div
                    key={session.id}
                    className={`flex items-center justify-between gap-3 rounded-lg border border-neutral-200 px-3 py-2 ${
                      canOpenDetail ? 'cursor-pointer transition-colors hover:bg-neutral-50' : ''
                    }`}
                    onClick={canOpenDetail ? () => navigate(`/dashboard/admin/calls/${session.id}`) : undefined}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-neutral-900">
                        {callerName} <span className="text-neutral-400">→</span> {proName}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {activityTypeLabels[session.callType] ?? 'Chiamata'} · {formatActivityDate(session.initiatedAt)}
                      </p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UserDashboard({
  firstName,
  todayFormatted,
}: {
  firstName: string;
  todayFormatted: string;
  user: ReturnType<typeof useAuth>['user'];
}) {
  const { data: wallet, isLoading: walletLoading } = useWallet();

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 p-8">
        <div className="relative z-10">
          <h1 className="text-2xl font-bold text-white">Benvenuto, {firstName}</h1>
          <p className="mt-1 text-brand-cyan">{todayFormatted}</p>
        </div>
        <div className="absolute right-0 top-0 h-full w-1/3 opacity-10">
          <svg viewBox="0 0 200 200" className="h-full w-full" fill="none">
            <circle cx="160" cy="40" r="60" fill="white" />
            <circle cx="100" cy="140" r="80" fill="white" />
            <circle cx="180" cy="160" r="40" fill="white" />
          </svg>
        </div>
      </div>

      {/* Wallet & Actions */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Wallet Balance */}
        <div className="rounded-xl border border-neutral-200 border-l-4 border-l-brand-teal bg-white p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-teal-50 text-brand-teal-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-neutral-600">Saldo Borsellino</p>
              {walletLoading ? (
                <Spinner size="sm" />
              ) : (
                <p className="text-2xl font-bold text-neutral-900">
                  {wallet ? `\u20AC ${((wallet.bonusBalance || 0) + wallet.balance).toFixed(2)}` : '\u2014'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Recharge CTA */}
        <Link
          to="/dashboard/wallet"
          className="flex items-center gap-4 rounded-xl border border-neutral-200/80 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-brand-teal/30"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-orange-50 text-brand-orange-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-neutral-900">Ricarica Borsellino</h3>
            <p className="text-sm text-neutral-600">Aggiungi fondi per le consulenze</p>
          </div>
          <svg className="h-5 w-5 shrink-0 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>

        {/* Free Minutes CTA */}
        {wallet && !wallet.freeMinutesUsed && !hasCompletedFirstRecharge(wallet) && (
          <Link
            to="/dashboard/wallet"
            className="flex items-center gap-4 rounded-xl border-2 border-brand-teal bg-brand-teal-50 p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-teal text-white">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-brand-teal-dark">7 Minuti Gratis!</h3>
              <p className="text-sm text-brand-teal-600">Riscatta i tuoi minuti omaggio</p>
            </div>
            <svg className="h-5 w-5 shrink-0 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        )}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-neutral-900">Azioni Rapide</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <QuickActionCard
            to="/dashboard/consultations"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            }
            title="Le Mie Consulenze"
            description="Visualizza lo storico delle consulenze"
            color="primary"
          />
          <QuickActionCard
            to="/dashboard/wallet"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
              </svg>
            }
            title="Borsellino"
            description="Gestisci il saldo e le ricariche"
            color="secondary"
          />
        </div>
      </div>
    </div>
  );
}

const colorMap = {
  primary: {
    border: 'border-l-brand-teal',
    bg: 'bg-brand-teal-50',
    text: 'text-brand-teal-600',
  },
  secondary: {
    border: 'border-l-brand-cyan',
    bg: 'bg-brand-cyan-light',
    text: 'text-brand-cyan-dark',
  },
  warning: {
    border: 'border-l-brand-orange',
    bg: 'bg-brand-orange-50',
    text: 'text-brand-orange-600',
  },
  info: {
    border: 'border-l-brand-navy',
    bg: 'bg-brand-navy-50',
    text: 'text-brand-navy',
  },
  violet: {
    border: 'border-l-brand-orange-dark',
    bg: 'bg-brand-orange-50',
    text: 'text-brand-orange-600',
  },
} as const;

type CardColor = keyof typeof colorMap;

function StatsCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | undefined;
  color: CardColor;
}) {
  const colors = colorMap[color];

  return (
    <div className={`rounded-xl border border-neutral-200/80 border-l-4 ${colors.border} bg-white p-5 shadow-sm`}>
      <div className="flex items-center gap-4">
        <div className={`flex h-10 w-10 items-center justify-center rounded-full ${colors.bg} ${colors.text}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-neutral-900">
            {value !== undefined ? value : '\u2014'}
          </p>
          <p className="text-sm text-neutral-600">{label}</p>
        </div>
      </div>
    </div>
  );
}

function QuickActionCard({
  to,
  icon,
  title,
  description,
  color,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  color: CardColor;
}) {
  const colors = colorMap[color];

  return (
    <Link
      to={to}
      className="flex items-center gap-4 rounded-xl border border-neutral-200/80 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-brand-teal/30"
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${colors.bg} ${colors.text}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold text-neutral-900">{title}</h3>
        <p className="text-sm text-neutral-600">{description}</p>
      </div>
      <svg className="h-5 w-5 shrink-0 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}
